/**
 * IXVerify v2 — Settings (ixv2 WP-3.3; plan §4 screens inventory: "Settings
 * (CRIO intake email, allow-list, require-second-check, saved recipients)").
 *
 * Three cards:
 *   - Report delivery: the site's CRIO document-intake address (the "one tap"
 *     CRIO copy at publish) and the require-second-check strict mode
 *     (default OFF — plan §15 Q4 / decision D3). ADMIN-ONLY writes; the
 *     backend enforces this again (403 IXV_ADMIN_REQUIRED).
 *   - External allow-list: the R3 PHI-egress boundary — the only external
 *     addresses reports may ever reach. Admin-managed.
 *   - Saved recipients: the site's address book for resends. Any entitled
 *     user may save/remove entries, but every entry must itself pass the R3
 *     rule server-side (site user / CRIO intake / allow-listed external).
 *
 * Non-admins see the configuration read-only and can still manage saved
 * recipients.
 */
import { useCallback, useEffect, useState } from 'react';
import { Settings as SettingsIcon, Mail, ShieldCheck, ListChecks, Users, Trash2, Plus } from 'lucide-react';
import {
  PageHeader,
  RefreshButton,
  ConfirmModal,
  useToast,
  primaryBtnCls,
  secondaryBtnCls,
  fieldCls,
} from '../../ui';
import { useAuth } from '../../contexts/AuthContext';
import {
  getSettings,
  updateSettings,
  listRecipients,
  addRecipient,
  deleteRecipient,
  errorMessage,
  isRecipientNotAllowed,
  IxvSavedRecipient,
} from '../lib/api';

const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';
const cardCls = 'rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800';

function CardTitle({ icon: Icon, children }: { icon: typeof Mail; children: React.ReactNode }) {
  return (
    <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
      <Icon className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
      {children}
    </h2>
  );
}

export default function Settings() {
  const { toast } = useToast();
  const { isAdmin, isSuperAdmin } = useAuth();
  const canManage = isAdmin || isSuperAdmin;


  const [recipients, setRecipients] = useState<IxvSavedRecipient[]>([]);
  const [loading, setLoading] = useState(true);

  // Delivery form
  const [crioEmail, setCrioEmail] = useState('');
  const [requireSecondCheck, setRequireSecondCheck] = useState(false);
  const [savingDelivery, setSavingDelivery] = useState(false);

  // Allow-list
  const [allowlist, setAllowlist] = useState<string[]>([]);
  const [newAllowEmail, setNewAllowEmail] = useState('');
  const [savingAllowlist, setSavingAllowlist] = useState(false);

  // Saved recipients
  const [newRecipient, setNewRecipient] = useState({ email: '', label: '' });
  const [addingRecipient, setAddingRecipient] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IxvSavedRecipient | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, recs] = await Promise.all([getSettings(), listRecipients()]);

      setCrioEmail(cfg.crio_intake_email || '');
      setRequireSecondCheck(cfg.require_second_check);
      setAllowlist(cfg.external_recipient_allowlist || []);
      setRecipients(recs);
    } catch (err) {
      toast(errorMessage(err, 'Failed to load settings'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const saveDelivery = async () => {
    setSavingDelivery(true);
    try {
      const next = await updateSettings({
        crio_intake_email: crioEmail.trim() || null,
        require_second_check: requireSecondCheck,
      });

      setCrioEmail(next.crio_intake_email || '');
      setRequireSecondCheck(next.require_second_check);
      toast('Delivery settings saved');
    } catch (err) {
      toast(errorMessage(err, 'Failed to save settings'), { type: 'error' });
    } finally {
      setSavingDelivery(false);
    }
  };

  const saveAllowlist = async (nextList: string[]) => {
    setSavingAllowlist(true);
    try {
      const next = await updateSettings({ external_recipient_allowlist: nextList });

      setAllowlist(next.external_recipient_allowlist || []);
      setNewAllowEmail('');
      toast('Allow-list updated');
    } catch (err) {
      toast(errorMessage(err, 'Failed to update the allow-list'), { type: 'error' });
    } finally {
      setSavingAllowlist(false);
    }
  };

  const addAllowEmail = () => {
    const email = newAllowEmail.trim().toLowerCase();
    if (!email) return;
    if (allowlist.includes(email)) {
      toast('That address is already on the allow-list', { type: 'info' });
      return;
    }
    saveAllowlist([...allowlist, email]);
  };

  const handleAddRecipient = async () => {
    const email = newRecipient.email.trim();
    if (!email) return;
    setAddingRecipient(true);
    try {
      const rec = await addRecipient({ email, label: newRecipient.label.trim() || undefined });
      setRecipients((prev) => [...prev, rec].sort((a, b) => a.email.localeCompare(b.email)));
      setNewRecipient({ email: '', label: '' });
      toast('Recipient saved');
    } catch (err) {
      if (isRecipientNotAllowed(err)) {
        toast('That address cannot receive reports — it must be a site user, the CRIO intake address, or allow-listed by an admin.', { type: 'error', duration: 8000 });
      } else {
        toast(errorMessage(err, 'Failed to save recipient'), { type: 'error' });
      }
    } finally {
      setAddingRecipient(false);
    }
  };

  const handleDeleteRecipient = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRecipient(deleteTarget.id);
      setRecipients((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast('Recipient removed');
    } catch (err) {
      toast(errorMessage(err, 'Failed to remove recipient'), { type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-4">
      <PageHeader
        icon={SettingsIcon}
        title="Settings"
        subtitle={canManage ? undefined : 'Read-only — site configuration is managed by administrators'}
        actions={<RefreshButton onClick={fetchAll} loading={loading} />}
      />

      {/* Report delivery */}
      <section className={cardCls} aria-label="Report delivery">
        <CardTitle icon={Mail}>Report delivery</CardTitle>
        <div className="space-y-4">
          <div>
            <label htmlFor="ixv-crio-email" className={labelCls}>CRIO document-intake email</label>
            <input
              id="ixv-crio-email"
              type="email"
              className={`${fieldCls} w-full`}
              placeholder="your-site@mail.us.clinicalresearch.io"
              value={crioEmail}
              onChange={(e) => setCrioEmail(e.target.value)}
              disabled={!canManage}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              When set, every published report is also emailed to this address (CRIO files it under the subject).
            </p>
          </div>
          <label className="flex cursor-pointer items-start gap-2">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
              checked={requireSecondCheck}
              onChange={(e) => setRequireSecondCheck(e.target.checked)}
              disabled={!canManage}
            />
            <span>
              <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                <ShieldCheck className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                Require second-person check
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Off (default): reports send immediately; a verifier countersigns as revision 2. On: strict mode for this site.
              </span>
            </span>
          </label>
          {canManage && (
            <button type="button" className={primaryBtnCls} onClick={saveDelivery} disabled={savingDelivery || loading}>
              Save delivery settings
            </button>
          )}
        </div>
      </section>

      {/* External allow-list */}
      <section className={cardCls} aria-label="External recipient allow-list">
        <CardTitle icon={ListChecks}>External recipient allow-list</CardTitle>
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          Reports contain PHI. Outside of site users and the CRIO intake address, only these
          admin-approved external addresses may ever receive one.
        </p>
        {allowlist.length === 0 ? (
          <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">No external addresses are allow-listed.</p>
        ) : (
          <ul className="mb-3 divide-y divide-gray-200 dark:divide-gray-700">
            {allowlist.map((email) => (
              <li key={email} className="flex items-center justify-between gap-2 py-2">
                <span className="text-sm text-gray-900 dark:text-gray-100">{email}</span>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => saveAllowlist(allowlist.filter((e) => e !== email))}
                    disabled={savingAllowlist}
                    aria-label={`Remove ${email} from the allow-list`}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-400 transition-colors duration-150 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-red-900/20 sm:h-8 sm:w-8"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="ixv-allow-add">External email to allow</label>
            <input
              id="ixv-allow-add"
              type="email"
              className={`${fieldCls} min-w-0 flex-1`}
              placeholder="sponsor-inbox@example.com"
              value={newAllowEmail}
              onChange={(e) => setNewAllowEmail(e.target.value)}
            />
            <button type="button" className={secondaryBtnCls} onClick={addAllowEmail} disabled={savingAllowlist || !newAllowEmail.trim()}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Allow
            </button>
          </div>
        )}
      </section>

      {/* Saved recipients */}
      <section className={cardCls} aria-label="Saved recipients">
        <CardTitle icon={Users}>Saved recipients</CardTitle>
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          The site's address book for report resends. Every entry must be a site user, the CRIO
          intake address, or an allow-listed external.
        </p>
        {recipients.length === 0 ? (
          <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">No saved recipients yet.</p>
        ) : (
          <ul className="mb-3 divide-y divide-gray-200 dark:divide-gray-700">
            {recipients.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-2 py-2">
                <span className="min-w-0">
                  <span className="block truncate text-sm text-gray-900 dark:text-gray-100">{r.email}</span>
                  {r.label && <span className="text-xs text-gray-500 dark:text-gray-400">{r.label}</span>}
                </span>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(r)}
                  aria-label={`Remove saved recipient ${r.email}`}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-400 transition-colors duration-150 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-red-900/20 sm:h-8 sm:w-8"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor="ixv-recipient-email">Recipient email</label>
          <input
            id="ixv-recipient-email"
            type="email"
            className={`${fieldCls} min-w-0 flex-1`}
            placeholder="coordinator@yoursite.com"
            value={newRecipient.email}
            onChange={(e) => setNewRecipient((p) => ({ ...p, email: e.target.value }))}
          />
          <label className="sr-only" htmlFor="ixv-recipient-label">Label (optional)</label>
          <input
            id="ixv-recipient-label"
            type="text"
            className={`${fieldCls} w-36`}
            placeholder="Label (optional)"
            value={newRecipient.label}
            onChange={(e) => setNewRecipient((p) => ({ ...p, label: e.target.value }))}
          />
          <button type="button" className={secondaryBtnCls} onClick={handleAddRecipient} disabled={addingRecipient || !newRecipient.email.trim()}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Save
          </button>
        </div>
      </section>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteRecipient}
        title="Remove saved recipient?"
        message={deleteTarget ? `${deleteTarget.email} will no longer appear in the resend picker.` : undefined}
        confirmLabel="Remove"
        danger
        loading={deleting}
      />
    </div>
  );
}
